export type UiLocale = 'uk' | 'en' | 'de' | 'fr';

export type UiStrings = {
  online: string;
  inputPlaceholder: string;
  popupCta: string;
  close: string;
  today: string;
  yesterday: string;
  statusSending: string;
  statusSent: string;
  statusDelivered: string;
  statusRead: string;
  statusFailed: string;
  retrySend: string;
  sendErrorGeneric: string;
  sendErrorUpload: string;
  sendErrorTooLarge: string;
  sendErrorUnsupported: string;
  managerBadge: string;
  recording: string;
  attachFile: string;
  openEmojiPicker: string;
  emojiSearchPlaceholder: string;
  sendVoice: string;
  cancel: string;
  mediaPlay: string;
  mediaPause: string;
  mediaStop: string;
  mediaDownload: string;
  mediaOpenPreview: string;
  mediaClosePreview: string;
  scrollToLatest: string;
  newMessages: (count: number) => string;
  connectionOffline: string;
  connectionReconnecting: string;
  connectionDisconnected: string;
  historyLoading: string;
  historyError: string;
  historyRetry: string;
  mediaLoadError: string;
  /** Short AI disclosure under the input (replaces legacy "Powered by"). */
  aiPoweredLabel: string;
};

const STRINGS: Record<UiLocale, UiStrings> = {
  uk: {
    online: 'онлайн',
    inputPlaceholder: 'Напишіть…',
    popupCta: 'Написати в чат',
    close: 'Закрити',
    today: 'Сьогодні',
    yesterday: 'Вчора',
    statusSending: 'Надсилається',
    statusSent: 'Надіслано',
    statusDelivered: 'Доставлено',
    statusRead: 'Прочитано',
    statusFailed: 'Не вдалося надіслати',
    retrySend: 'Повторити відправку',
    sendErrorGeneric: 'Не вдалося надіслати повідомлення. Спробуйте ще раз.',
    sendErrorUpload: 'Не вдалося завантажити зображення. Спробуйте ще раз.',
    sendErrorTooLarge: 'Зображення завелике (макс. 10 МБ).',
    sendErrorUnsupported: 'Цей формат зображення не підтримується.',
    managerBadge: 'Менеджер',
    recording: 'Запис…',
    attachFile: 'Прикріпити файл',
    openEmojiPicker: 'Смайли',
    emojiSearchPlaceholder: 'Пошук…',
    sendVoice: 'Надіслати',
    cancel: 'Скасувати',
    mediaPlay: 'Відтворити',
    mediaPause: 'Пауза',
    mediaStop: 'Зупинити',
    mediaDownload: 'Завантажити',
    mediaOpenPreview: 'Відкрити',
    mediaClosePreview: 'Закрити',
    scrollToLatest: 'До останніх повідомлень',
    newMessages: (count) =>
      count === 1 ? '1 нове повідомлення' : `${count} нових повідомлень`,
    connectionOffline: 'Немає інтернету. Повідомлення буде надіслано після відновлення.',
    connectionReconnecting: 'Підключення…',
    connectionDisconnected: 'Немає з’єднання. Вхідні повідомлення можуть затримуватися.',
    historyLoading: 'Завантаження історії…',
    historyError: 'Не вдалося завантажити історію',
    historyRetry: 'Спробувати знову',
    mediaLoadError: 'Не вдалося завантажити',
    aiPoweredLabel: 'Працює на ШІ',
  },
  en: {
    online: 'online',
    inputPlaceholder: 'Message…',
    popupCta: 'Chat with us',
    close: 'Close',
    today: 'Today',
    yesterday: 'Yesterday',
    statusSending: 'Sending',
    statusSent: 'Sent',
    statusDelivered: 'Delivered',
    statusRead: 'Read',
    statusFailed: 'Failed to send',
    retrySend: 'Retry send',
    sendErrorGeneric: 'Could not send the message. Please try again.',
    sendErrorUpload: 'Could not upload the image. Please try again.',
    sendErrorTooLarge: 'Image is too large (max 10 MB).',
    sendErrorUnsupported: 'This image format is not supported.',
    managerBadge: 'Manager',
    recording: 'Recording…',
    attachFile: 'Attach file',
    openEmojiPicker: 'Emoji',
    emojiSearchPlaceholder: 'Search…',
    sendVoice: 'Send',
    cancel: 'Cancel',
    mediaPlay: 'Play',
    mediaPause: 'Pause',
    mediaStop: 'Stop',
    mediaDownload: 'Download',
    mediaOpenPreview: 'Open',
    mediaClosePreview: 'Close',
    scrollToLatest: 'Scroll to latest',
    newMessages: (count) =>
      count === 1 ? '1 new message' : `${count} new messages`,
    connectionOffline: 'You’re offline. Messages will send when you’re back online.',
    connectionReconnecting: 'Reconnecting…',
    connectionDisconnected: 'Connection lost. Incoming messages may be delayed.',
    historyLoading: 'Loading chat history…',
    historyError: 'Could not load chat history',
    historyRetry: 'Try again',
    mediaLoadError: 'Could not load media',
    aiPoweredLabel: 'AI-powered',
  },
  de: {
    online: 'online',
    inputPlaceholder: 'Nachricht…',
    popupCta: 'Chat starten',
    close: 'Schließen',
    today: 'Heute',
    yesterday: 'Gestern',
    statusSending: 'Wird gesendet',
    statusSent: 'Gesendet',
    statusDelivered: 'Zugestellt',
    statusRead: 'Gelesen',
    statusFailed: 'Senden fehlgeschlagen',
    retrySend: 'Erneut senden',
    sendErrorGeneric: 'Nachricht konnte nicht gesendet werden. Bitte erneut versuchen.',
    sendErrorUpload: 'Bild konnte nicht hochgeladen werden. Bitte erneut versuchen.',
    sendErrorTooLarge: 'Bild ist zu groß (max. 10 MB).',
    sendErrorUnsupported: 'Dieses Bildformat wird nicht unterstützt.',
    managerBadge: 'Manager',
    recording: 'Aufnahme…',
    attachFile: 'Datei anhängen',
    openEmojiPicker: 'Emoji',
    emojiSearchPlaceholder: 'Suchen…',
    sendVoice: 'Senden',
    cancel: 'Abbrechen',
    mediaPlay: 'Abspielen',
    mediaPause: 'Pause',
    mediaStop: 'Stopp',
    mediaDownload: 'Herunterladen',
    mediaOpenPreview: 'Öffnen',
    mediaClosePreview: 'Schließen',
    scrollToLatest: 'Zu neuesten Nachrichten',
    newMessages: (count) =>
      count === 1 ? '1 neue Nachricht' : `${count} neue Nachrichten`,
    connectionOffline: 'Offline. Nachrichten werden nach der Verbindung gesendet.',
    connectionReconnecting: 'Verbindung wird wiederhergestellt…',
    connectionDisconnected: 'Verbindung unterbrochen. Eingehende Nachrichten können verzögert sein.',
    historyLoading: 'Chatverlauf wird geladen…',
    historyError: 'Chatverlauf konnte nicht geladen werden',
    historyRetry: 'Erneut versuchen',
    mediaLoadError: 'Medien konnten nicht geladen werden',
    aiPoweredLabel: 'KI-gestützt',
  },
  fr: {
    online: 'en ligne',
    inputPlaceholder: 'Message…',
    popupCta: 'Discuter',
    close: 'Fermer',
    today: "Aujourd'hui",
    yesterday: 'Hier',
    statusSending: 'Envoi en cours',
    statusSent: 'Envoyé',
    statusDelivered: 'Distribué',
    statusRead: 'Lu',
    statusFailed: "Échec de l'envoi",
    retrySend: 'Renvoyer',
    sendErrorGeneric: "Impossible d'envoyer le message. Réessayez.",
    sendErrorUpload: "Impossible de téléverser l'image. Réessayez.",
    sendErrorTooLarge: "L'image est trop volumineuse (max. 10 Mo).",
    sendErrorUnsupported: "Ce format d'image n'est pas pris en charge.",
    managerBadge: 'Manager',
    recording: 'Enregistrement…',
    attachFile: 'Joindre un fichier',
    openEmojiPicker: 'Émojis',
    emojiSearchPlaceholder: 'Rechercher…',
    sendVoice: 'Envoyer',
    cancel: 'Annuler',
    mediaPlay: 'Lecture',
    mediaPause: 'Pause',
    mediaStop: 'Arrêter',
    mediaDownload: 'Télécharger',
    mediaOpenPreview: 'Ouvrir',
    mediaClosePreview: 'Fermer',
    scrollToLatest: 'Derniers messages',
    newMessages: (count) =>
      count === 1 ? '1 nouveau message' : `${count} nouveaux messages`,
    connectionOffline: 'Hors ligne. Les messages seront envoyés à la reconnexion.',
    connectionReconnecting: 'Reconnexion…',
    connectionDisconnected: 'Connexion perdue. Les messages entrants peuvent être retardés.',
    historyLoading: 'Chargement de l’historique…',
    historyError: 'Impossible de charger l’historique',
    historyRetry: 'Réessayer',
    mediaLoadError: 'Impossible de charger le média',
    aiPoweredLabel: 'Assistance IA',
  },
};

export function getUiStrings(locale: string): UiStrings {
  const key = locale as UiLocale;
  return STRINGS[key] ?? STRINGS.en;
}
